import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, fileUrl, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Megaphone, ExternalLink, Check, AlertCircle, MousePointerClick, Eye, CalendarClock, MapPin, Share2 } from "lucide-react";
import { Pagination, SortableHeader, MultiSelectStatusFilter, ColumnVisibility, compareValues } from "../components/DataTable";
import ConfirmDialog from "../components/ConfirmDialog";
import ChipList from "../components/ChipList";
import ImageCropper from "../components/ImageCropper";
import useLocalStorageState from "../hooks/useLocalStorageState";
import { useAuth } from "../contexts/AuthContext";

const STATUS_OPTIONS = ["pending", "approved", "rejected"];
const PAGE_OPTIONS = ["home", "services", "portfolio", "contact"];
const SLOT_OPTIONS = ["marquee-top", "hero-banner", "sidebar"];

// Which slot uses which image field (informs the form UI labels).
const HORIZONTAL_SLOTS = ["marquee-top", "hero-banner"];
const VERTICAL_SLOTS = ["sidebar"];

// (Background image cropping now uses free aspect — see ImageCropper.)

const FOCAL_OPTIONS = [
  "top left", "top center", "top right",
  "center left", "center center", "center right",
  "bottom left", "bottom center", "bottom right",
];

const COLUMNS = [
  { key: "idx", label: "#", required: true },
  { key: "bg", label: "Bg" },
  { key: "restaurant_name", label: "Restaurant", required: true },
  { key: "services", label: "Services" },
  { key: "placements", label: "Placements" },
  { key: "address", label: "Address" },
  { key: "status", label: "Status" },
  { key: "is_active", label: "Active" },
  { key: "priority", label: "Priority" },
  { key: "schedule", label: "Schedule" },
  { key: "views", label: "Views" },
  { key: "clicks", label: "Clicks" },
  { key: "updated_by_label", label: "Modified by" },
  { key: "updated_at", label: "Modified on" },
];

const emptyForm = {
  restaurant_name: "",
  restaurant_id: "",
  services: [],
  logo_image_id: "",
  background_image_id: "",
  sidebar_image_id: "",
  image_focal_point: "center center",
  website_url: "",
  address: "",
  owner_email: "",
  owner_whatsapp: "",
  is_active: true,
  priority: 0,
  placements: [{ page: "home", slot: "marquee-top" }],
  starts_at: "",
  ends_at: "",
};

const statusBadge = {
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  rejected: "bg-rose-100 text-rose-800 border-rose-200",
};

function fmtDt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}
function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}

/**
 * Compute a human-readable schedule status given start/end ISO timestamps.
 * Returns { label, tone } where tone ∈ "ok"|"warn"|"muted"|"done"|"upcoming".
 */
function scheduleStatus(startIso, endIso) {
  const now = Date.now();
  const start = startIso ? new Date(startIso).getTime() : null;
  const end = endIso ? new Date(endIso).getTime() : null;
  const MS_PER_DAY = 86400000;

  if (start && now < start) {
    const days = Math.ceil((start - now) / MS_PER_DAY);
    return { label: days <= 1 ? "Starts today" : `Starts in ${days} days`, tone: "upcoming" };
  }
  if (end && now > end) {
    const days = Math.floor((now - end) / MS_PER_DAY);
    return { label: days < 1 ? "Just ended" : `Ended ${days}d ago`, tone: "done" };
  }
  if (end) {
    const days = Math.ceil((end - now) / MS_PER_DAY);
    if (days <= 0) return { label: "Ends today", tone: "warn" };
    if (days === 1) return { label: "1 day left", tone: "warn" };
    if (days <= 7) return { label: `${days} days left`, tone: "warn" };
    return { label: `${days} days left`, tone: "ok" };
  }
  if (start) return { label: "Live · open-ended", tone: "ok" };
  return null;
}

const TONE_CLS = {
  ok:       "bg-emerald-50 text-emerald-700 border-emerald-200",
  warn:     "bg-amber-50 text-amber-800 border-amber-200",
  done:     "bg-zinc-100 text-zinc-600 border-zinc-200",
  upcoming: "bg-sky-50 text-sky-700 border-sky-200",
  muted:    "bg-zinc-50 text-zinc-500 border-zinc-200",
};
function toLocalInput(iso) {
  // YYYY-MM-DDTHH:mm for <input type=datetime-local>
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function localInputToISO(s) {
  if (!s) return "";
  const d = new Date(s);
  if (isNaN(d.getTime())) return "";
  return d.toISOString();
}

export default function AdminAds() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [ads, setAds] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [statusSel, setStatusSel] = useState(new Set());

  const [sortKey, setSortKey] = useState("priority");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [hiddenCols, setHiddenCols] = useLocalStorageState("admin_ads_hidden_cols_v1", new Set(["address", "updated_by_label", "updated_at"]));
  const toggleCol = (k) => setHiddenCols((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const showCol = (k) => !hiddenCols.has(k);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [adsRes, svcRes, restRes] = await Promise.all([
        api.get("/admin/ads"), api.get("/services"), api.get("/restaurants").catch(() => ({ data: [] })),
      ]);
      setAds(adsRes.data);
      setAllServices(svcRes.data || []);
      setRestaurants(restRes.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleStatus = (s) => { setPage(1); setStatusSel((prev) => { const n = new Set(prev); if (n.has(s)) n.delete(s); else n.add(s); return n; }); };
  const onSort = (key) => { if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc")); else { setSortKey(key); setSortDir("asc"); } };

  // Auto-populate the website link from the selected restaurant's Zomato URL
  // (primary). Fall back to Swiggy URL if no Zomato is set. Only fills when the
  // field is empty — preserves anything the operator has already typed.
  useEffect(() => {
    if (!form.restaurant_id || form.website_url) return;
    const r = restaurants.find((x) => x.id === form.restaurant_id);
    if (!r) return;
    const next = (r.zomato_url || r.swiggy_url || "").trim();
    if (next) setForm((f) => ({ ...f, website_url: next }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.restaurant_id, restaurants]);

  const filtered = useMemo(() => {
    if (statusSel.size === 0) return ads;
    return ads.filter((a) => statusSel.has(a.status));
  }, [ads, statusSel]);
  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => compareValues(a[sortKey], b[sortKey], sortDir));
    return arr;
  }, [filtered, sortKey, sortDir]);
  const total = sorted.length;
  const paged = useMemo(() => sorted.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize), [sorted, page, pageSize]);
  useEffect(() => { setPage(1); }, [pageSize, statusSel.size]);

  const openNew = () => { setForm(emptyForm); setEditing("new"); };
  const openEdit = (ad) => {
    setForm({
      restaurant_name: ad.restaurant_name || "",
      restaurant_id: ad.restaurant_id || "",
      services: ad.services || [],
      logo_image_id: ad.logo_image_id || "",
      background_image_id: ad.background_image_id || "",
      sidebar_image_id: ad.sidebar_image_id || "",
      image_focal_point: ad.image_focal_point || "center center",
      website_url: ad.website_url || "",
      address: ad.address || "",
      owner_email: ad.owner_email || "",
      owner_whatsapp: ad.owner_whatsapp || "",
      is_active: ad.is_active !== false,
      priority: Number(ad.priority ?? 0),
      placements: (ad.placements && ad.placements.length > 0) ? ad.placements : [{ page: "home", slot: "marquee-top" }],
      starts_at: toLocalInput(ad.starts_at),
      ends_at: toLocalInput(ad.ends_at),
    });
    setEditing(ad);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.restaurant_name.trim()) { toast.error("Restaurant name is required"); return; }
    if (form.placements.length === 0) { toast.error("Add at least one placement"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        services: form.services.filter(Boolean),
        logo_image_id: form.logo_image_id || null,
        background_image_id: form.background_image_id || null,
        sidebar_image_id: form.sidebar_image_id || null,
        website_url: (form.website_url || "").trim(),
        address: (form.address || "").trim(),
        owner_email: (form.owner_email || "").trim(),
        owner_whatsapp: (form.owner_whatsapp || "").trim(),
        starts_at: localInputToISO(form.starts_at) || null,
        ends_at: localInputToISO(form.ends_at) || null,
      };
      if (editing === "new") {
        await api.post("/admin/ads", payload);
        toast.success(isAdmin ? "Advertisement created & approved" : "Submitted for admin approval");
      } else {
        await api.patch(`/admin/ads/${editing.id}`, payload);
        toast.success("Advertisement updated");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  const toggleActive = async (ad) => {
    try {
      await api.patch(`/admin/ads/${ad.id}`, {
        restaurant_name: ad.restaurant_name,
        restaurant_id: ad.restaurant_id || null,
        services: ad.services || [],
        logo_image_id: ad.logo_image_id || null,
        background_image_id: ad.background_image_id || null,
        sidebar_image_id: ad.sidebar_image_id || null,
        image_focal_point: ad.image_focal_point || "center center",
        website_url: ad.website_url || "",
        address: ad.address || "",
        owner_email: ad.owner_email || "",
        owner_whatsapp: ad.owner_whatsapp || "",
        is_active: !ad.is_active,
        priority: ad.priority || 0,
        placements: ad.placements || [{ page: "home", slot: "marquee-top" }],
        starts_at: ad.starts_at || null,
        ends_at: ad.ends_at || null,
      });
      toast.success(ad.is_active ? "Deactivated" : "Activated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const shareReport = async (ad) => {
    try {
      const { data } = await api.get(`/admin/ads/${ad.id}/report`);
      const lines = [
        `📊 *Monthly Status Report — ${data.restaurant_name}*`,
        `Period: last 30 days (lifetime totals shown)`,
        ``,
        `👀 Views: ${data.totals_lifetime.views.toLocaleString()}`,
        `🖱 Clicks: ${data.totals_lifetime.clicks.toLocaleString()}`,
        `📈 CTR: ${data.totals_lifetime.ctr_percent}%`,
        ``,
        `Status: ${(data.current_status || "").toUpperCase()}${data.is_active ? "" : " (inactive)"}`,
        `Placements: ${(data.placements || []).map((p) => `${p.page}·${p.slot}`).join(", ") || "—"}`,
      ];
      if (data.recent_events?.length) {
        lines.push("");
        lines.push(`Recent activity (${data.recent_events.length}):`);
        for (const ev of data.recent_events.slice(0, 5)) {
          lines.push(`• ${new Date(ev.created_at).toLocaleDateString()} — ${ev.summary}`);
        }
      }
      lines.push("");
      lines.push("— Sent by RestroSetup admin");
      const text = lines.join("\n");
      const phone = (ad.owner_whatsapp || "").replace(/\D/g, "");
      if (!phone) {
        // Copy report to clipboard as fallback
        await navigator.clipboard.writeText(text).catch(() => {});
        toast.warning("No owner WhatsApp number. Report copied to clipboard.");
        return;
      }
      const url = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
      window.open(url, "_blank", "noopener,noreferrer");
      toast.success("Opening WhatsApp...");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to build report");
    }
  };

  const approve = async (ad) => {
    try { await api.post(`/admin/ads/${ad.id}/approve`); toast.success("Approved"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const submitReject = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/ads/${rejecting.id}/reject`, { reason: rejectReason });
      toast.success("Rejected"); setRejecting(null); setRejectReason(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const askRemove = (ad) => setDeleting(ad);
  const confirmRemove = async () => {
    if (!deleting) return;
    try { await api.delete(`/admin/ads/${deleting.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
    finally { setDeleting(null); }
  };

  // Background image upload allows any aspect ratio (free size).

  return (
    <div data-testid="admin-ads-page" className="p-6 lg:p-10 w-full">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-sm uppercase tracking-[0.2em] text-[#525252]">Manage</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2 flex items-center gap-3">
            <Megaphone size={28} strokeWidth={1.5} /> Advertisements
          </h1>
          {!isAdmin && (
            <p className="text-xs text-[#525252] mt-2 inline-flex items-center gap-1.5"><AlertCircle size={12}/> New ads require admin approval before they appear on the public site.</p>
          )}
        </div>
        <button onClick={openNew} data-testid="add-ad-button"
                className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white px-5 py-2.5 rounded-sm font-medium inline-flex items-center gap-2">
          <Plus size={16}/> Add Advertisement
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="text-xs uppercase tracking-[0.15em] text-[#525252]">Status:</div>
        <MultiSelectStatusFilter options={STATUS_OPTIONS} selected={statusSel} onToggle={toggleStatus} testIdPrefix="ad-filter"/>
        {statusSel.size > 0 && (
          <button data-testid="ad-clear-filter" onClick={() => setStatusSel(new Set())} className="text-xs text-[#525252] hover:text-[var(--brand-primary)] underline">Clear</button>
        )}
        <div className="ml-auto"><ColumnVisibility columns={COLUMNS} hidden={hiddenCols} onToggle={toggleCol} testId="ad-cols"/></div>
      </div>

      {loading ? <div className="text-[#525252]">Loading...</div>
       : sorted.length === 0 ? (
        <div className="bg-white border border-black/10 p-16 text-center text-[#525252]" data-testid="ads-empty">No advertisements yet.</div>
       ) : (
        <>
          <div className="bg-white border border-black/10 overflow-x-auto">
            <table className="w-full text-sm" data-testid="ads-table">
              <thead>
                <tr className="border-b border-black/10 bg-[#FAFAFA]">
                  {showCol("idx") && <th className="px-4 py-3 text-left text-xs uppercase tracking-[0.15em] text-[#525252] w-12">#</th>}
                  {showCol("bg") && <th className="px-4 py-3 text-left text-xs uppercase tracking-[0.15em] text-[#525252]">Bg</th>}
                  {showCol("restaurant_name") && <th className="px-4 py-3 text-left"><SortableHeader label="Restaurant" sortKey="restaurant_name" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("services") && <th className="px-4 py-3 text-left text-xs uppercase tracking-[0.15em] text-[#525252]">Services</th>}
                  {showCol("placements") && <th className="px-4 py-3 text-left text-xs uppercase tracking-[0.15em] text-[#525252]">Placements</th>}
                  {showCol("address") && <th className="px-4 py-3 text-left text-xs uppercase tracking-[0.15em] text-[#525252]">Address</th>}
                  {showCol("status") && <th className="px-4 py-3 text-left"><SortableHeader label="Status" sortKey="status" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("is_active") && <th className="px-4 py-3 text-left"><SortableHeader label="Active" sortKey="is_active" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("priority") && <th className="px-4 py-3 text-left"><SortableHeader label="Priority" sortKey="priority" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("schedule") && <th className="px-4 py-3 text-left text-xs uppercase tracking-[0.15em] text-[#525252]">Schedule</th>}
                  {showCol("views") && <th className="px-4 py-3 text-left"><SortableHeader label="Views" sortKey="view_count" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("clicks") && <th className="px-4 py-3 text-left"><SortableHeader label="Clicks" sortKey="click_count" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("updated_by_label") && <th className="px-4 py-3 text-left"><SortableHeader label="Modified by" sortKey="updated_by_label" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("updated_at") && <th className="px-4 py-3 text-left"><SortableHeader label="Modified on" sortKey="updated_at" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  <th className="px-4 py-3 text-right text-xs uppercase tracking-[0.15em] text-[#525252]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((a, i) => (
                  <tr key={a.id} data-testid={`ad-row-${a.id}`} className="border-b border-black/5 hover:bg-[#FAFAFA]">
                    {showCol("idx") && <td className="px-4 py-3 text-[#525252] text-xs">{(page - 1) * pageSize + i + 1}</td>}
                    {showCol("bg") && (
                      <td className="px-4 py-3">
                        {(a.background_image_id || a.sidebar_image_id) ? (
                          <div className="flex items-center gap-1">
                            {a.background_image_id && (
                              <img src={fileUrl(a.background_image_id)} alt="" loading="lazy"
                                   style={{ objectPosition: a.image_focal_point || "center center" }}
                                   title="Wide image (marquee/hero)"
                                   className="w-12 h-8 object-cover rounded-sm bg-[#FAFAFA] border border-black/5"/>
                            )}
                            {a.sidebar_image_id && (
                              <img src={fileUrl(a.sidebar_image_id)} alt="" loading="lazy"
                                   title="Vertical image (sidebar)"
                                   className="w-6 h-8 object-cover rounded-sm bg-[#FAFAFA] border border-black/5"/>
                            )}
                          </div>
                        ) : <span className="text-xs text-rose-700">missing</span>}
                      </td>
                    )}
                    {showCol("restaurant_name") && (
                      <td className="px-4 py-3 font-medium max-w-[220px]" title={a.restaurant_name}>
                        <div className="truncate">{a.restaurant_name}</div>
                        {a.website_url && (
                          <a href={a.website_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#525252] inline-flex items-center gap-1 hover:text-[var(--brand-primary)]">
                            <ExternalLink size={10}/> link
                          </a>
                        )}
                      </td>
                    )}
                    {showCol("services") && (
                      <td className="px-4 py-3">
                        <div className="max-w-[240px]">
                          <ChipList items={a.services || []} limit={2} testId={`ad-services-${a.id}`}/>
                        </div>
                      </td>
                    )}
                    {showCol("placements") && (
                      <td className="px-4 py-3">
                        <div className="max-w-[260px]">
                          <ChipList
                            items={(a.placements || []).map((p, j) => ({ label: `${p.page} · ${p.slot}`, key: `${p.page}-${p.slot}-${j}` }))}
                            limit={2}
                            testId={`ad-placements-${a.id}`}
                            chipClassName="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-[#FAFAFA] text-[#525252] border border-black/10 rounded-sm"
                          />
                        </div>
                      </td>
                    )}
                    {showCol("address") && (
                      <td className="px-4 py-3 text-[#525252] text-xs">
                        {a.address ? (
                          <a href={`https://maps.google.com/?q=${encodeURIComponent(a.address)}`}
                             target="_blank" rel="noopener noreferrer"
                             data-testid={`ad-address-${a.id}`}
                             title={a.address}
                             className="inline-flex items-center gap-1 hover:text-[var(--brand-primary)] max-w-[220px] truncate">
                            <MapPin size={11}/> <span className="truncate">{a.address}</span>
                          </a>
                        ) : <span className="italic">—</span>}
                      </td>
                    )}
                    {showCol("status") && (
                      <td className="px-4 py-3">
                        <span data-testid={`ad-status-${a.id}`} className={`inline-block px-2 py-0.5 text-[10px] uppercase rounded-sm border ${statusBadge[a.status] || ""}`}>{a.status}</span>
                        {a.status === "rejected" && a.reject_reason && (
                          <div className="text-[10px] text-rose-700 mt-1 italic max-w-[200px]" title={a.reject_reason}>{a.reject_reason}</div>
                        )}
                      </td>
                    )}
                    {showCol("is_active") && (
                      <td className="px-4 py-3">
                        <button type="button" onClick={() => toggleActive(a)} data-testid={`toggle-active-${a.id}`} title="Click to toggle"
                                className={`inline-block px-2 py-0.5 text-[10px] uppercase rounded-sm border cursor-pointer hover:opacity-80 ${
                                  a.is_active ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-zinc-100 text-zinc-700 border-zinc-200"
                                }`}>{a.is_active ? "yes" : "no"}</button>
                      </td>
                    )}
                    {showCol("priority") && <td className="px-4 py-3 font-mono text-xs text-[#525252]">{a.priority ?? 0}</td>}
                    {showCol("schedule") && (
                      <td className="px-4 py-3 text-[11px] text-[#525252] whitespace-nowrap" data-testid={`ad-schedule-${a.id}`}>
                        {(() => {
                          const status = scheduleStatus(a.starts_at, a.ends_at);
                          if (!status && !a.starts_at && !a.ends_at) {
                            return <span className="italic text-[10px]">always-on</span>;
                          }
                          return (
                            <div className="flex flex-col gap-0.5">
                              {status && (
                                <span data-testid={`ad-schedule-pill-${a.id}`}
                                      className={`inline-flex items-center gap-1 self-start px-1.5 py-0.5 text-[10px] uppercase tracking-wider rounded-sm border ${TONE_CLS[status.tone]}`}>
                                  <CalendarClock size={10}/> {status.label}
                                </span>
                              )}
                              <span className="text-[10px] text-[#9CA3AF]">
                                {a.starts_at ? fmtDate(a.starts_at) : "—"} → {a.ends_at ? fmtDate(a.ends_at) : "—"}
                              </span>
                            </div>
                          );
                        })()}
                      </td>
                    )}
                    {showCol("views") && (
                      <td className="px-4 py-3"><span data-testid={`ad-views-${a.id}`} className="inline-flex items-center gap-1 text-xs font-mono text-[#525252]"><Eye size={12}/> {a.view_count || 0}</span></td>
                    )}
                    {showCol("clicks") && (
                      <td className="px-4 py-3"><span data-testid={`ad-clicks-${a.id}`} className="inline-flex items-center gap-1 text-xs font-mono text-[#525252]"><MousePointerClick size={12}/> {a.click_count || 0}</span></td>
                    )}
                    {showCol("updated_by_label") && <td className="px-4 py-3 text-[#525252] text-xs truncate max-w-[200px]" title={a.updated_by_label}>{a.updated_by_label || "—"}</td>}
                    {showCol("updated_at") && <td className="px-4 py-3 text-[#525252] text-xs whitespace-nowrap">{fmtDt(a.updated_at)}</td>}
                    <td className="px-4 py-3">
                      <div className="flex gap-1 justify-end">
                        {isAdmin && a.status === "pending" && (
                          <>
                            <button onClick={() => approve(a)} data-testid={`approve-ad-${a.id}`} title="Approve" className="p-2 hover:bg-emerald-50 text-emerald-700 rounded-sm"><Check size={16}/></button>
                            <button onClick={() => setRejecting(a)} data-testid={`reject-ad-${a.id}`} title="Reject" className="p-2 hover:bg-rose-50 text-rose-700 rounded-sm"><X size={16}/></button>
                          </>
                        )}
                        <button onClick={() => shareReport(a)} data-testid={`share-report-${a.id}`} title={a.owner_whatsapp ? "Share report via WhatsApp" : "Copy report (no WhatsApp number)"} className="p-2 hover:bg-emerald-50 text-emerald-700 rounded-sm"><Share2 size={16}/></button>
                        <button onClick={() => openEdit(a)} data-testid={`edit-ad-${a.id}`} title="Edit" className="p-2 hover:bg-black/5 rounded-sm"><Pencil size={16}/></button>
                        {isAdmin && <button onClick={() => askRemove(a)} data-testid={`delete-ad-${a.id}`} title="Delete" className="p-2 hover:bg-[var(--brand-primary)]/5 text-[var(--brand-primary)] rounded-sm"><Trash2 size={16}/></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} onPageSizeChange={setPageSize}/>
        </>
       )}

      {editing && (
        <div className="fixed inset-0 z-50 flex" data-testid="ad-modal">
          <div className="flex-1 bg-black/40" onClick={() => setEditing(null)} />
          <form onSubmit={submit} className="w-full max-w-lg bg-white h-full overflow-y-auto p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl font-bold">{editing === "new" ? "New Advertisement" : "Edit Advertisement"}</h2>
              <button type="button" onClick={() => setEditing(null)} className="p-2 hover:bg-black/5"><X size={18}/></button>
            </div>

            {!isAdmin && editing === "new" && (
              <div className="mb-5 p-3 rounded-sm bg-amber-50 border border-amber-200 text-amber-800 text-xs flex items-start gap-2">
                <AlertCircle size={14} className="mt-0.5 shrink-0"/>
                <span>New advertisements are submitted as <strong>pending</strong> and become visible only after an admin approves them.</span>
              </div>
            )}

            <div className="space-y-5">
              <Field label="Restaurant name *">
                <input data-testid="ad-name" required type="text" value={form.restaurant_name}
                       onChange={(e) => setForm({ ...form, restaurant_name: e.target.value })} className={inputCls}/>
              </Field>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium">Linked restaurant <span className="text-[#525252] font-normal">(optional)</span></label>
                  {form.restaurant_id && (
                    <button type="button" data-testid="ad-prefill-from-restaurant"
                            onClick={() => {
                              const r = restaurants.find((x) => x.id === form.restaurant_id);
                              if (!r) return;
                              setForm((f) => ({
                                ...f,
                                restaurant_name: r.name || f.restaurant_name,
                                address: r.address || f.address,
                                website_url: r.zomato_url || r.swiggy_url || f.website_url,
                                owner_email: r.email || f.owner_email,
                                owner_whatsapp: r.phone || f.owner_whatsapp,
                                services: r.services?.length ? r.services : f.services,
                                logo_image_id: r.logo_image_id || f.logo_image_id,
                              }));
                              toast.success("Pre-filled from restaurant");
                            }}
                            className="text-[10px] uppercase tracking-wider text-[var(--brand-primary)] hover:underline">
                      Use restaurant details
                    </button>
                  )}
                </div>
                <select
                  value={form.restaurant_id || ""}
                  data-testid="ad-restaurant"
                  onChange={(e) => setForm({ ...form, restaurant_id: e.target.value })}
                  className={inputCls}
                >
                  <option value="">— None —</option>
                  {restaurants.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}{r.is_active ? "" : " (inactive)"}</option>
                  ))}
                </select>
                <p className="text-[10px] text-[#525252] mt-1">Linking surfaces this ad in the restaurant's analytics drawer.</p>
              </div>

              <PlacementsEditor value={form.placements} onChange={(arr) => setForm({ ...form, placements: arr })}/>

              <ImageCropper
                label="Wide image — used for Marquee top + Hero banner"
                fileId={form.background_image_id}
                onUpload={(id) => setForm({ ...form, background_image_id: id })}
                onClear={() => setForm({ ...form, background_image_id: "" })}
                testId="ad-bg"
              />

              {form.background_image_id && (
                <FocalPointPicker
                  imageId={form.background_image_id}
                  value={form.image_focal_point}
                  onChange={(v) => setForm({ ...form, image_focal_point: v })}
                />
              )}

              <ImageCropper
                label="Vertical image — used for Sidebar"
                fileId={form.sidebar_image_id}
                onUpload={(id) => setForm({ ...form, sidebar_image_id: id })}
                onClear={() => setForm({ ...form, sidebar_image_id: "" })}
                testId="ad-sidebar-bg"
              />

              <ImageCropper
                label="Logo (optional)"
                fileId={form.logo_image_id}
                onUpload={(id) => setForm({ ...form, logo_image_id: id })}
                onClear={() => setForm({ ...form, logo_image_id: "" })}
                testId="ad-logo"
              />

              <ServiceTagsPicker value={form.services} options={allServices} onChange={(arr) => setForm({ ...form, services: arr })}/>

              <Field label="Website URL">
                <input data-testid="ad-url" type="url" value={form.website_url} placeholder="https://..."
                       onChange={(e) => setForm({ ...form, website_url: e.target.value })} className={inputCls}/>
                <p className="mt-1 text-[10px] text-[#525252]">Auto-fills from the linked restaurant's Zomato link (or Swiggy if Zomato is empty).</p>
              </Field>

              <Field label={<span className="inline-flex items-center gap-1"><MapPin size={12}/> Location / address <span className="text-[#525252] font-normal">(optional)</span></span>}>
                <input data-testid="ad-address" type="text" value={form.address} placeholder="e.g., Connaught Place, New Delhi"
                       maxLength={300}
                       onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls}/>
                {form.address && (
                  <a href={`https://maps.google.com/?q=${encodeURIComponent(form.address)}`} target="_blank" rel="noopener noreferrer"
                     className="mt-1 inline-flex items-center gap-1 text-[10px] text-[#525252] hover:text-[var(--brand-primary)]">
                    <ExternalLink size={10}/> Preview on Google Maps
                  </a>
                )}
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label={<span>Owner email <span className="text-[#525252] font-normal">(opt)</span></span>}>
                  <input data-testid="ad-owner-email" type="email" value={form.owner_email} placeholder="owner@…"
                         onChange={(e) => setForm({ ...form, owner_email: e.target.value })} className={inputCls}/>
                </Field>
                <Field label={<span>Owner WhatsApp <span className="text-[#525252] font-normal">(opt)</span></span>}>
                  <input data-testid="ad-owner-whatsapp" type="tel" value={form.owner_whatsapp} placeholder="+919812345678"
                         onChange={(e) => setForm({ ...form, owner_whatsapp: e.target.value })} className={inputCls}/>
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Priority (lower = first)">
                  <input data-testid="ad-priority" type="number" value={form.priority}
                         onChange={(e) => setForm({ ...form, priority: Number(e.target.value) || 0 })} className={inputCls}/>
                </Field>
                <Field label="Active">
                  <label className="inline-flex items-center gap-2 mt-2">
                    <input type="checkbox" data-testid="ad-active" checked={form.is_active}
                           onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="w-4 h-4"/>
                    <span className="text-sm text-[#525252]">{form.is_active ? "Visible (when approved)" : "Hidden"}</span>
                  </label>
                </Field>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 inline-flex items-center gap-2"><CalendarClock size={14}/> Schedule (optional)</label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="block text-[10px] uppercase tracking-wider text-[#525252] mb-1">Starts at</span>
                    <input data-testid="ad-starts-at" type="datetime-local" value={form.starts_at}
                           onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className={inputCls}/>
                  </div>
                  <div>
                    <span className="block text-[10px] uppercase tracking-wider text-[#525252] mb-1">Ends at</span>
                    <input data-testid="ad-ends-at" type="datetime-local" value={form.ends_at}
                           onChange={(e) => setForm({ ...form, ends_at: e.target.value })} className={inputCls}/>
                  </div>
                </div>
                <p className="mt-1 text-[10px] text-[#525252]">Leave blank for always-on. Times are in your local time-zone; stored as UTC.</p>
              </div>
            </div>

            <div className="mt-8 flex gap-3">
              <button type="button" onClick={() => setEditing(null)}
                      className="flex-1 bg-white text-[#0A0A0A] border border-black/10 hover:bg-black/5 px-6 py-3 rounded-sm font-medium" data-testid="ad-cancel">Cancel</button>
              <button type="submit" disabled={saving} data-testid="ad-submit"
                      className="flex-1 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white px-6 py-3 rounded-sm font-medium disabled:opacity-60">
                {saveBtnLabel(saving, editing === "new", isAdmin)}
              </button>
            </div>
          </form>
        </div>
      )}

      {rejecting && (
        <div className="fixed inset-0 z-50 flex" data-testid="reject-ad-modal">
          <div className="flex-1 bg-black/40" onClick={() => { setRejecting(null); setRejectReason(""); }} />
          <form onSubmit={submitReject} className="w-full max-w-md bg-white h-full overflow-y-auto p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl font-bold">Reject Advertisement</h2>
              <button type="button" onClick={() => { setRejecting(null); setRejectReason(""); }} className="p-2 hover:bg-black/5"><X size={18}/></button>
            </div>
            <p className="text-sm text-[#525252] mb-4"><strong>{rejecting.restaurant_name}</strong> will be marked as rejected.</p>
            <textarea data-testid="reject-reason" rows={4} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Reason (optional)..." className={inputCls}/>
            <button type="submit" data-testid="submit-reject-ad" className="mt-6 w-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white px-6 py-3 rounded-sm font-medium">Reject</button>
          </form>
        </div>
      )}

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => { if (!o) setDeleting(null); }}
        title="Delete this advertisement?"
        description={deleting ? `“${deleting.restaurant_name}” will be permanently removed. This action can't be undone.` : ""}
        confirmText="Delete"
        destructive
        onConfirm={confirmRemove}
        testId="delete-ad-dialog"
      />
    </div>
  );
}

const inputCls = "w-full bg-white border border-black/10 rounded-sm px-3 py-2 focus:ring-2 focus:ring-[var(--brand-primary)]/20 focus:border-[var(--brand-primary)] outline-none";
function Field({ label, children }) {
  return (<div><label className="block text-sm font-medium mb-2">{label}</label>{children}</div>);
}

function saveBtnLabel(saving, isNew, isAdmin) {
  if (saving) return "Saving...";
  if (!isNew) return "Save changes";
  return isAdmin ? "Create" : "Submit for approval";
}


function FocalPointPicker({ imageId, value, onChange }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-2">Image focal point</label>
      <div className="grid grid-cols-[160px,1fr] gap-3 items-start">
        <div className="grid grid-cols-3 gap-1 border border-black/10 rounded-sm p-2 bg-[#FAFAFA]" data-testid="focal-grid">
          {FOCAL_OPTIONS.map((opt) => {
            const selected = value === opt;
            return (
              <button key={opt} type="button" data-testid={`focal-${opt.replace(" ", "-")}`} title={opt}
                      onClick={() => onChange(opt)}
                      aria-label={opt}
                      className={`h-8 rounded-sm border transition-colors ${selected ? "bg-[var(--brand-primary)] border-[var(--brand-primary)]" : "bg-white border-black/10 hover:border-black/30"}`}/>
            );
          })}
        </div>
        <div className="aspect-[16/9] w-full overflow-hidden rounded-sm border border-black/10 bg-[#FAFAFA]">
          <img src={fileUrl(imageId)} alt="focal preview" loading="lazy"
               style={{ objectPosition: value }}
               className="w-full h-full object-cover"/>
        </div>
      </div>
      <p className="mt-1 text-[10px] text-[#525252]">Selected: <span className="font-mono">{value}</span></p>
    </div>
  );
}

function ServiceTagsPicker({ value = [], options = [], onChange }) {
  const toggle = (t) => {
    if (value.includes(t)) onChange(value.filter((v) => v !== t));
    else onChange([...value, t]);
  };
  return (
    <div>
      <label className="block text-sm font-medium mb-2">Service tags</label>
      {options.length === 0 ? (
        <p className="text-xs text-[#525252]" data-testid="ad-services-empty">No services available. Create services first.</p>
      ) : (
        <div className="border border-black/10 rounded-sm p-3 bg-[#FAFAFA] max-h-48 overflow-y-auto" data-testid="ad-services-picker">
          <div className="grid grid-cols-1 gap-1.5">
            {options.map((o) => {
              const checked = value.includes(o.title);
              return (
                <label key={o.id || o.title} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white px-2 py-1.5 rounded-sm">
                  <input type="checkbox" data-testid={`ad-service-${o.slug || o.title}`} checked={checked} onChange={() => toggle(o.title)} className="w-4 h-4"/>
                  <span className="flex-1">{o.title}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function PlacementsEditor({ value, onChange }) {
  // value: [{page, slot}, ...]
  // Any combination of pages × slots is allowed. The form provides two image
  // uploads — one for horizontal slots (marquee-top + hero-banner) and one for
  // the vertical sidebar — so a single ad record can run across all slots.
  const has = (pg, sl) => value.some((p) => p.page === pg && p.slot === sl);
  const toggle = (pg, sl) => {
    if (has(pg, sl)) onChange(value.filter((p) => !(p.page === pg && p.slot === sl)));
    else onChange([...value, { page: pg, slot: sl }]);
  };
  const clearAll = () => onChange([]);
  const selectAll = () => {
    const all = PAGE_OPTIONS.flatMap((pg) => SLOT_OPTIONS.map((sl) => ({ page: pg, slot: sl })));
    onChange(all);
  };
  return (
    <div data-testid="placements-editor">
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium">Display locations *</label>
        <div className="flex items-center gap-3">
          <button type="button" onClick={selectAll} data-testid="placements-select-all"
                  className="text-[10px] uppercase tracking-wider text-[#525252] hover:text-[var(--brand-primary)]">
            Select all
          </button>
          {value.length > 0 && (
            <button type="button" onClick={clearAll} data-testid="placements-clear"
                    className="text-[10px] uppercase tracking-wider text-[#525252] hover:text-[var(--brand-primary)]">
              Clear all
            </button>
          )}
        </div>
      </div>
      <div className="border border-black/10 rounded-sm overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-[#FAFAFA] border-b border-black/10">
              <th className="px-2 py-2 text-left text-[#525252] uppercase tracking-wider">Page \ Slot</th>
              {SLOT_OPTIONS.map((s) => (
                <th key={s} className="px-2 py-2 text-center uppercase tracking-wider text-[#525252]">
                  {s.replace("-", " ")}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PAGE_OPTIONS.map((pg) => (
              <tr key={pg} className="border-b border-black/5">
                <td className="px-2 py-2 capitalize font-medium">{pg}</td>
                {SLOT_OPTIONS.map((sl) => (
                  <td key={sl} className="px-2 py-2 text-center">
                    <input type="checkbox" data-testid={`placement-${pg}-${sl}`}
                           checked={has(pg, sl)} onChange={() => toggle(pg, sl)}
                           className="w-4 h-4"/>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-1 text-[10px] text-[#525252]">
        Pick any combination of pages × slots. The wide image is used for{" "}
        <span className="font-mono">{HORIZONTAL_SLOTS.join(", ")}</span>; the vertical image is used for{" "}
        <span className="font-mono">{VERTICAL_SLOTS.join(", ")}</span>.
      </p>
      {value.length === 0 && <p className="mt-1 text-[10px] text-rose-700">Select at least one cell</p>}
    </div>
  );
}
